import { Check, ExternalLink, SkipForward, X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ReviewAction } from "@/types/review";

interface ReviewControlsProps {
  onAction: (action: ReviewAction) => void;
  notes: string;
  onNotesChange: (notes: string) => void;
  currentUrl: string;
  isLoading?: boolean;
}

export default function ReviewControls({
  onAction,
  notes,
  onNotesChange,
  currentUrl,
  isLoading = false,
}: ReviewControlsProps) {
  // Keyboard shortcuts are now handled globally in the main page component

  const openInNewTab = () => {
    if (currentUrl) {
      window.open(currentUrl, "_blank", "noopener,noreferrer");
    }
  };

  return (
    <Card>
      <CardContent className="pt-6">
        <div className="space-y-4">
          {/* Action Buttons */}
          <div className="grid grid-cols-3 gap-3">
            <Button
              onClick={() => onAction("correct")}
              disabled={isLoading}
              className="h-16 bg-green-600 hover:bg-green-700 text-white flex flex-col gap-1"
            >
              <Check className="h-6 w-6" />
              <span className="text-sm">Correct (C)</span>
            </Button>

            <Button
              onClick={() => onAction("incorrect")}
              disabled={isLoading}
              className="h-16 bg-red-600 hover:bg-red-700 text-white flex flex-col gap-1"
            >
              <X className="h-6 w-6" />
              <span className="text-sm">Incorrect (I)</span>
            </Button>

            <Button
              onClick={() => onAction("skip")}
              disabled={isLoading}
              variant="outline"
              className="h-16 flex flex-col gap-1"
            >
              <SkipForward className="h-6 w-6" />
              <span className="text-sm">Skip (S)</span>
            </Button>
          </div>

          {/* External Link Button */}
          {currentUrl && (
            <Button
              onClick={openInNewTab}
              variant="outline"
              className="w-full"
              disabled={!currentUrl}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              Open in New Tab
            </Button>
          )}

          {/* Notes Section */}
          <div className="space-y-2">
            <Label htmlFor="review-notes">Notes (Optional)</Label>
            <textarea
              id="review-notes"
              value={notes}
              onChange={(e) => onNotesChange(e.target.value)}
              placeholder="Add any observations about this URL..."
              className="w-full h-24 px-3 py-2 border border-gray-300 rounded-md resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              disabled={isLoading}
            />
          </div>

          {/* Keyboard Shortcuts Help */}
          <div className="text-xs text-gray-500 border-t pt-3">
            <div className="font-medium mb-1">Keyboard Shortcuts:</div>
            <div className="grid grid-cols-3 gap-2">
              <span>C or 1: Correct</span>
              <span>I or 2: Incorrect</span>
              <span>S or 3: Skip</span>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
